import { ref, shallowRef, watchEffect } from 'vue';
import type { RouteLocationNormalized } from 'vue-router';

import persistanceHelper from '@exchange/helpers/persistance-helper';
import { authService } from '@exchange/libs/auth/service/src';
import { modalService } from '@exchange/libs/modals/src';
import WebRest from '@exchange/libs/rest-api/web-api';
import { settingsService } from '@exchange/libs/settings/service/src';
import { launchdarkly } from '@exchange/libs/utils/launchdarkly/src';
import type { TermsAndConditionsFlag } from '@exchange/libs/utils/launchdarkly/src/lib/defaults';
import { logger } from '@exchange/libs/utils/simple-logger/src';
import { checkIsBiometricRoute } from '@exchange/routing';

import GeneralToSModal from './GeneralToSModal.vue';
import TermsAndConditionsModal from './TermsAndConditionsModal.vue';

const TERMS_AND_CONDITIONS_KEY_PREFIX = 'terms-and-conditions';
const TERMS_AND_CONDITIONS_REMIND_ME_PERIOD_MS = 1000 * 60 * 60 * 4; // 4 hours

export const useGlobalConfirmationModals = (route: RouteLocationNormalized) => {
  const modalGeneralToSKey = ref<string>();
  const modalTermAndConditionsKey = ref<string>();

  let showGeneralToSModalPromise = Promise.resolve();
  let showTermAndConditionsModalPromise = Promise.resolve();

  const resolveGeneralToS = () => {
    showGeneralToSModalPromise = Promise.resolve();

    return showGeneralToSModalPromise;
  };
  const hideGeneralToSModal = () => {
    if (modalGeneralToSKey.value) {
      modalService.hide(modalGeneralToSKey.value);
      modalGeneralToSKey.value = undefined;
    }
  };
  const showGeneralToSModal = async () => {
    const { showRiskConfirmation } = settingsService.complianceInfo;

    if (showRiskConfirmation === false) {
      if (modalGeneralToSKey.value) {
        hideGeneralToSModal();
      }

      return resolveGeneralToS();
    }

    if (showRiskConfirmation === undefined || (showRiskConfirmation && modalGeneralToSKey.value)) {
      return resolveGeneralToS();
    }

    return new Promise<void>((resolve) => {
      modalGeneralToSKey.value = modalService.show(
        shallowRef(GeneralToSModal),
        {
          accept: () => {
            settingsService.setSettings({
              path: 'riskAgreement',
              value: new Date().toString(),
            });
            hideGeneralToSModal();
            resolveGeneralToS();
            resolve();
          },
        },
        {},
        { canClose: false },
      );
    });
  };
  const handleGeneralToS = () => {
    showGeneralToSModalPromise = showGeneralToSModalPromise.then(showGeneralToSModal);

    return showGeneralToSModalPromise;
  };

  const resolveTermAndConditionsModal = () => {
    showTermAndConditionsModalPromise = Promise.resolve();

    return showTermAndConditionsModalPromise;
  };
  const hideTermAndConditionsModal = () => {
    if (modalTermAndConditionsKey.value) {
      modalService.hide(modalTermAndConditionsKey.value);
      modalTermAndConditionsKey.value = undefined;
    }
  };

  let isEvaluatingTermAndConditionsModal = false;

  const showTermAndConditionsModal = async () => {
    // Skip if the modal is already open or being evaluated
    if (modalTermAndConditionsKey.value || isEvaluatingTermAndConditionsModal) {
      return resolveTermAndConditionsModal();
    }

    isEvaluatingTermAndConditionsModal = true;

    /*
     * There is a flag (terms-and-conditions-date) in launchdarkly for managing terms and conditions changes.
     *
     * The flag contains:
     * - publishedDate: the date the terms and conditions were published
     * - effectiveDate: the date the terms and conditions will be enforced
     *
     * We only need to check for an existing agreement if we're before the effective date.
     * If we're after the effective date, we don't need to check for an existing agreement.
     * If we're on or before the effective date, we show the modal and store the user's acknowledgement.
     * The modal can be configured to only show from the published date by setting the showFromPublishedDate flag to true.
     *
     * The flag has two modes:
     * - acknowledge: the user needs to acknowledge the terms and conditions (ok button)
     * - accept: the user needs to accept the terms and conditions (accept button with remind me later)
     *
     * For special cases, we can also override the content of the modal from the flag by specifying an html key.
     * The html key should be a record of supported locales and their corresponding html content.
     *
     * Example flag value:
     * {
     *   "publishedDate": "2025-06-04",
     *   "effectiveDate": "2025-07-01",
     *   "mode": "accept",
     *   "html": {
     *     "en": "<p>Terms and conditions</p>",
     *     "de": "<p>Nutzungsbedingungen</p>"
     *   },
     *   "showFromPublishedDate": true
     * }
     */
    try {
      const termsConfig = (await launchdarkly.getWhenReady('terms-and-conditions-date')) as TermsAndConditionsFlag;
      if (!termsConfig?.publishedDate || !termsConfig?.effectiveDate) {
        logger.warn('No terms date configuration available from LaunchDarkly');
        return await resolveTermAndConditionsModal();
      }

      const currentDate = new Date();
      const effectiveDate = new Date(termsConfig.effectiveDate);
      const publishedDate = new Date(termsConfig.publishedDate);

      // Skip if show from published date is true and we're before the published date
      if (termsConfig.showFromPublishedDate && currentDate < publishedDate) {
        return await resolveTermAndConditionsModal();
      }

      // Skip if we're after the effective date
      if (currentDate > effectiveDate) {
        return await resolveTermAndConditionsModal();
      }

      const key = `${TERMS_AND_CONDITIONS_KEY_PREFIX}-${termsConfig.publishedDate}:reminder`;
      const remindMeLaterTimestamp = persistanceHelper.getObjFromJSON(key, undefined);

      // Skip if the user has opted to be reminded later and the reminder date is in the future
      if (remindMeLaterTimestamp) {
        const remindMeLaterDate = new Date(remindMeLaterTimestamp);

        if (currentDate < remindMeLaterDate) {
          return await resolveTermAndConditionsModal();
        }

        persistanceHelper.localStorageDelete(key);
      }

      // Check for existing agreement and skip if it exists
      const existingAgreement = await WebRest.Customer.Agreement.getTermsAgreement(termsConfig.publishedDate);
      if (existingAgreement) {
        return await resolveTermAndConditionsModal();
      }

      return await new Promise<void>((resolve) => {
        modalTermAndConditionsKey.value = modalService.show(
          shallowRef(TermsAndConditionsModal),
          {
            accept: async () => {
              try {
                await WebRest.Customer.Agreement.setTermsAgreement(termsConfig.publishedDate);
                hideTermAndConditionsModal();
                resolveTermAndConditionsModal();
                resolve();
              } catch (e) {
                logger.error('Accepting T&C failed: ', e);
              }
            },
            remindMeLater: () => {
              try {
                const remindMeLaterDate = new Date(Date.now() + TERMS_AND_CONDITIONS_REMIND_ME_PERIOD_MS);

                hideTermAndConditionsModal();
                resolveTermAndConditionsModal();
                persistanceHelper.localstorageSet(key, remindMeLaterDate.toISOString());
                resolve();
              } catch (e) {
                logger.error('T&C remind me later failed: ', e);
              }
            },
            effectiveDate: termsConfig.effectiveDate,
            html: termsConfig.html,
            mode: termsConfig.mode,
          },
          {},
          { canClose: false },
        );
      });
    } finally {
      isEvaluatingTermAndConditionsModal = false;
    }
  };

  const handleTermAndConditionsModal = () => {
    showTermAndConditionsModalPromise = showTermAndConditionsModalPromise.then(showTermAndConditionsModal);

    return showTermAndConditionsModalPromise;
  };

  watchEffect(async () => {
    if (checkIsBiometricRoute(route)) {
      return;
    }

    try {
      if (authService.isAuthenticated) {
        await handleTermAndConditionsModal();
      }

      if (settingsService.complianceInfo.showRiskConfirmation) {
        await handleGeneralToS();
      }

      if (settingsService.complianceInfo.showRiskConfirmation === false) {
        resolveGeneralToS();
        hideGeneralToSModal();
      }
    } catch (error) {
      logger.error('Error in global confirmation modals:', error);
    }
  });
};

export default useGlobalConfirmationModals;
